/**
 * Special-rate contracts (BL-05), mirroring `backend/src/domain/contracts/specialRate.ts`.
 * An administrator flags a contract with Delphi's *Speciala* box when writing it, or later with
 * *Special commission* on the Contracts page, which also sets a new daily commission. A flagged
 * contract gets no discount at payment.
 */

/** Upper bound of the daily commission, in percent per day; the server checks the same. */
export const MAX_SPECIAL_COMMISSION_PER_DAY = 100;

/** Active (`null` / `''`), passive (`'!'`) and expired (`'!!'`): the statuses Delphi enables. */
const OPEN_STATUSES: ReadonlySet<string> = new Set(['', '!', '!!']);

/** Whether *Special commission* may be applied to a contract in this status. */
export const canSetSpecialRate = (status: string | null | undefined): boolean =>
    OPEN_STATUSES.has(status ?? '');

/**
 * The typed daily commission, accepting a decimal comma; `null` when it is empty, not a number
 * or outside 0-100.
 */
export const parseSpecialCommission = (text: string): number | null => {
    const normalised = text.trim().replace(',', '.');
    if (normalised === '') {
        return null;
    }
    const value = Number(normalised);
    const inRange = value >= 0 && value <= MAX_SPECIAL_COMMISSION_PER_DAY;
    return Number.isFinite(value) && inRange ? value : null;
};

/** The field's starting text: the contract's current daily commission, or empty. */
export const formatSpecialCommission = (commissionPerDay: number | string | undefined): string => {
    const value = Number(commissionPerDay);
    return commissionPerDay === undefined || Number.isNaN(value) ? '' : String(value);
};
